
/**
 * A series of helpers to print partial contents
 */
export const printHelper = {
    /**
     * Prints an image data URI
     *
     * @param data A data URI
     * @param title The title of the print document
     * @param options.delay The amount of time to delay performing the print action
     */
    image: (data: string | HTMLImageElement, title: string, {delay, fit = true}: {delay?: number; fit?: boolean} = {delay: 50}): void => {
        const iframe = document.createElement('iframe');

        // Make the print frame invisible
        iframe.style.visibility = 'hidden';
        // Prevent the print frame from messing with the scroll bars
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        // Add the print frame to the document

        document.body.appendChild(iframe);
        // This is needed for FireFox

        const doc = iframe.contentWindow?.document;

        const imgSrc = data instanceof HTMLImageElement ? data.src : data;
        const safeTitle = title ?? '';

        // Write a complete HTML document with DOCTYPE to avoid quirks mode.
        // Inline styles ensure margin/padding resets apply before the image renders,
        // preventing a blank first page on long images.
        doc.open();
        const imgStyle = 'display:block;max-width:100%;page-break-before:avoid;break-before:avoid;'
            + (fit ? 'max-height:100vh;object-fit:contain;margin:auto;' : 'margin:0;');
        const bodyStyle = fit
            ? 'html,body{margin:0;padding:0;height:100%;}body{display:flex;align-items:center;justify-content:center;}'
            : 'html,body{margin:0;padding:0;}';
        doc.write(
            '<!DOCTYPE html><html><head><title>' + safeTitle + '</title>'
            + '<style>' + bodyStyle + 'img{' + imgStyle + '}</style>'
            + '</head><body><img src="' + imgSrc + '"></body></html>'
        );
        doc.close();

        const actualDelay = Math.max(delay, 50);

        // Delay printing long enough for the image to safely be loaded
        setTimeout(() => {
            try {
                if (!doc.execCommand('print', false, null)) {
                    iframe.contentWindow?.print();
                }
            } catch {
                iframe.contentWindow?.print();
            }
        }, actualDelay);

        let removeDelay = Math.max(actualDelay, 200);

        // iOS/ipadOS devices take a bit more time to make the print action happen
        const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) &&
            !(window as any).MSStream;

        if (isIOS && removeDelay < 2000) {
            removeDelay = 2000;
        }

        // Delay removing the print frame long enough for the browsers to safely prepare the contents of the print action
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, removeDelay);
    }
};
